import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  apagarConversao,
  carregarConfiguracao,
  criarConversao,
  definirPrincipal,
  incluirAcao,
  renomearConversao,
  retirarAcao,
  type AcaoConversao,
  type ConfiguracaoMarca,
} from '../api/conversoes'
import { PLATAFORMAS, nomePlataforma } from '../api/trafegoPago'
import { MetasMarca } from '../components/MetasMarca'
import { useFiltros } from '../state/FiltrosContext'
import { conferirEscolha, sobreposicoes } from '../utils/acoesConversao'
import { formatarConversoes, formatarInteiro } from '../utils/formato'
import { intervaloDoPeriodo } from '../utils/periodo'

type Dialogo =
  | { tipo: 'criar'; nome: string }
  | { tipo: 'renomear'; id: string; nome: string }
  | { tipo: 'apagar'; id: string; nome: string }
  | null

/**
 * O Admin escolhe, marca a marca, quais ações das plataformas contam como conversão.
 * Tudo salva na hora e recalcula o histórico inteiro — o coletor guarda todas as ações.
 */
export function ConversoesPage() {
  const { marcaId: marcaDoFiltro, marcas } = useFiltros()
  const [marcaId, setMarcaId] = useState(marcaDoFiltro)
  const marca = marcaId || marcas[0]?.id || ''
  const [config, setConfig] = useState<ConfiguracaoMarca | null>(null)
  const [selecionada, setSelecionada] = useState<string>('')
  const [carregando, setCarregando] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [dialogo, setDialogo] = useState<Dialogo>(null)
  const periodoUso = useMemo(() => intervaloDoPeriodo('30d'), [])

  const recarregar = useCallback(async () => {
    if (!marca) return
    setCarregando(true)
    try {
      const c = await carregarConfiguracao(marca, periodoUso)
      setConfig(c)
      setErro(null)
      // Mantém a seleção se ela ainda existe; senão, a principal ou a primeira.
      setSelecionada((atual) =>
        c.conversoes.some((x) => x.id === atual)
          ? atual
          : (c.conversoes.find((x) => x.principal) ?? c.conversoes[0])?.id ?? '',
      )
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setCarregando(false)
    }
  }, [marca, periodoUso])

  useEffect(() => {
    setConfig(null)
    recarregar()
  }, [recarregar])

  /** Executa uma alteração, mostra o erro se houver e recarrega do banco. */
  const alterar = async (acao: () => Promise<void>) => {
    setSalvando(true)
    try {
      await acao()
      setErro(null)
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      await recarregar()
      setSalvando(false)
    }
  }

  const conversao = config?.conversoes.find((c) => c.id === selecionada)
  const escolhidas = conversao ? (config?.vinculos.get(conversao.id) ?? new Set<string>()) : new Set<string>()

  // Ações agrupadas por conta, contas na mesma ordem de plataforma do painel (Meta, depois Google).
  const porConta = useMemo(() => {
    const grupos = new Map<string, AcaoConversao[]>()
    for (const a of config?.acoes ?? []) grupos.set(a.contaId, [...(grupos.get(a.contaId) ?? []), a])
    const ordem = (p: string) => {
      const i = PLATAFORMAS.findIndex((x) => x.id === p)
      return i === -1 ? PLATAFORMAS.length : i
    }
    return [...grupos.values()].sort(
      (a, b) => ordem(a[0].plataforma) - ordem(b[0].plataforma) || a[0].contaNome.localeCompare(b[0].contaNome, 'pt-BR'),
    )
  }, [config])

  const acoesEscolhidas = (config?.acoes ?? []).filter((a) => escolhidas.has(a.id))
  const alertas = sobreposicoes(acoesEscolhidas)
  // Só as de anúncio se comparam com cliques nos anúncios (as do GA4 vêm do site).
  const conversoesAnuncio = acoesEscolhidas
    .filter((a) => a.plataforma === 'meta_ads' || a.plataforma === 'google_ads')
    .reduce((s, a) => s + (config?.uso.get(a.id) ?? 0), 0)
  const conferencia = conferirEscolha(acoesEscolhidas, conversoesAnuncio, config?.cliques ?? 0)
  const nomes = (lista: { nome: string }[]) => lista.map((a) => `“${a.nome}”`).join(', ')

  const confirmarDialogo = () => {
    if (!dialogo) return
    const d = dialogo
    setDialogo(null)
    if (d.tipo === 'criar') {
      // A primeira conversão da marca já nasce principal.
      alterar(() => criarConversao(marca, d.nome, !config?.conversoes.some((c) => c.principal)))
    } else if (d.tipo === 'renomear') {
      alterar(() => renomearConversao(d.id, d.nome))
    } else {
      alterar(() => apagarConversao(d.id))
    }
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }}>
          Conversões
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
          Escolha, em cada marca, quais ações do Meta Ads e do Google Ads e quais eventos do GA4 contam como
          conversão. Cada painel soma só a parte da sua fonte: o tráfego pago usa as ações de anúncio, o orgânico usa os
          eventos do GA4. A principal é a dos números principais (custo por conversão, ROAS, taxa de conversão). Toda
          mudança salva na hora e recalcula o histórico inteiro.
        </Typography>
      </Box>

      <TextField
        select
        size="small"
        label="Marca"
        value={marca}
        onChange={(e) => setMarcaId(e.target.value)}
        sx={{ maxWidth: 280 }}
      >
        {marcas.map((m) => (
          <MenuItem key={m.id} value={m.id}>
            {m.nome}
          </MenuItem>
        ))}
      </TextField>

      {erro ? (
        <Alert severity="error" onClose={() => setErro(null)}>
          {erro}
        </Alert>
      ) : null}

      {marca ? <MetasMarca marcaId={marca} nomeConversao={config?.conversoes.find((c) => c.principal)?.nome ?? null} /> : null}

      {!config ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
          {carregando ? <CircularProgress /> : null}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '300px minmax(0, 1fr)' },
            alignItems: 'start',
            opacity: salvando ? 0.6 : 1,
            transition: 'opacity 160ms ease',
          }}
        >
          <Paper variant="outlined">
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
                Conversões da marca
              </Typography>
              <Button size="small" onClick={() => setDialogo({ tipo: 'criar', nome: '' })} sx={{ textTransform: 'none' }}>
                Nova
              </Button>
            </Stack>
            {config.conversoes.length ? (
              <List dense disablePadding>
                {config.conversoes.map((c) => (
                  <ListItemButton key={c.id} selected={c.id === selecionada} onClick={() => setSelecionada(c.id)}>
                    <ListItemText
                      primary={c.nome}
                      secondary={(() => {
                        const n = config.vinculos.get(c.id)?.size ?? 0
                        return n === 0 ? 'Nenhuma ação' : n === 1 ? '1 ação' : `${n} ações`
                      })()}
                    />
                    {c.principal ? <Chip size="small" label="Principal" color="primary" variant="outlined" /> : null}
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
                Nenhuma conversão ainda. Crie a primeira — por exemplo, "Vendas" ou "Leads".
              </Typography>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            {!conversao ? (
              <Typography color="text.secondary">Crie ou escolha uma conversão à esquerda.</Typography>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif', mr: 'auto' }}>
                    O que conta como “{conversao.nome}”
                  </Typography>
                  {!conversao.principal ? (
                    <Button size="small" variant="outlined" onClick={() => alterar(() => definirPrincipal(conversao.id))} sx={{ textTransform: 'none' }}>
                      Tornar principal
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    onClick={() => setDialogo({ tipo: 'renomear', id: conversao.id, nome: conversao.nome })}
                    sx={{ textTransform: 'none' }}
                  >
                    Renomear
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setDialogo({ tipo: 'apagar', id: conversao.id, nome: conversao.nome })}
                    sx={{ textTransform: 'none' }}
                  >
                    Apagar
                  </Button>
                </Stack>

                {conversoesAnuncio > 0 || config.cliques > 0 ? (
                  <Typography variant="body2" color={conferencia.maisQueCliques ? 'warning.main' : 'text.secondary'}>
                    Nos últimos 30 dias, a escolha atual dá <strong>{formatarConversoes(conversoesAnuncio)}</strong>{' '}
                    {conversoesAnuncio === 1 ? 'conversão' : 'conversões'} nos anúncios, para{' '}
                    <strong>{formatarInteiro(config.cliques)}</strong> cliques.
                  </Typography>
                ) : null}

                {conferencia.maisQueCliques ? (
                  <Alert severity="warning">
                    <strong>Mais conversões do que cliques.</strong> Quase sempre é ação de engajamento marcada (cada curtida
                    ou visualização vira uma conversão) ou a mesma ação contada duas vezes. Deixe marcada só a ação que é o
                    resultado da marca.
                  </Alert>
                ) : null}

                {alertas.map((grupo) => (
                  <Alert key={grupo.map((a) => a.id).join()} severity="warning">
                    <strong>{grupo.map((a) => a.nome).join(' e ')}</strong> provavelmente contam a mesma coisa — com todas
                    marcadas, a mesma conversão é somada mais de uma vez. Deixe só uma.
                  </Alert>
                ))}

                {conferencia.engajamento.length ? (
                  <Alert severity="warning">
                    {nomes(conferencia.engajamento)} {conferencia.engajamento.length === 1 ? 'mede' : 'medem'} atenção
                    (curtidas, visualizações, visitas), não resultado. Como conversão, infla o número e baixa o custo por
                    conversão artificialmente.
                  </Alert>
                ) : null}

                {conferencia.trafego.length ? (
                  <Alert severity="info">
                    {nomes(conferencia.trafego)} é clique ou visita, não venda nem contato. Só faz sentido como conversão se o
                    objetivo da marca for levar tráfego — e, nesse caso, deixe só essa ação marcada.
                  </Alert>
                ) : null}

                {porConta.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Esta marca ainda não tem ações de conversão. Elas aparecem aqui quando o coletor ler as contas de
                    anúncio da marca pela primeira vez.
                  </Typography>
                ) : (
                  porConta.map((acoes) => (
                    <Box key={acoes[0].contaId}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {nomePlataforma(acoes[0].plataforma)} · {acoes[0].contaNome}
                      </Typography>
                      <Stack>
                        {acoes.map((a) => {
                          const usada = escolhidas.has(a.id)
                          const uso = config.uso.get(a.id) ?? 0
                          return (
                            <Stack
                              key={a.id}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              component="label"
                              sx={{ py: 0.5, cursor: salvando ? 'default' : 'pointer' }}
                            >
                              <Checkbox
                                checked={usada}
                                disabled={salvando}
                                onChange={() =>
                                  alterar(() =>
                                    usada ? retirarAcao(conversao.id, a.id) : incluirAcao(conversao.id, a.id, marca),
                                  )
                                }
                                inputProps={{ 'aria-label': a.nome }}
                              />
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {a.nome}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                  {a.idExterno}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                color={uso ? 'text.secondary' : 'warning.main'}
                                sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                              >
                                {uso ? `${formatarConversoes(uso)} em 30 dias` : 'Nenhuma em 30 dias'}
                              </Typography>
                            </Stack>
                          )
                        })}
                      </Stack>
                    </Box>
                  ))
                )}
              </Stack>
            )}
          </Paper>
        </Box>
      )}

      <Dialog open={Boolean(dialogo)} onClose={() => setDialogo(null)} fullWidth maxWidth="xs">
        {dialogo?.tipo === 'apagar' ? (
          <>
            <DialogTitle>Apagar “{dialogo.nome}”?</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Os painéis deixam de mostrar essa conversão. Os dados coletados continuam guardados — dá para criar de
                novo quando quiser.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogo(null)}>Cancelar</Button>
              <Button color="error" variant="contained" onClick={confirmarDialogo}>
                Apagar
              </Button>
            </DialogActions>
          </>
        ) : dialogo ? (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              if (dialogo.nome.trim()) confirmarDialogo()
            }}
          >
            <DialogTitle>{dialogo.tipo === 'criar' ? 'Nova conversão' : 'Renomear conversão'}</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                label="Nome"
                placeholder="Ex.: Vendas, Leads, Conversas no WhatsApp"
                value={dialogo.nome}
                onChange={(e) => setDialogo({ ...dialogo, nome: e.target.value })}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogo(null)}>Cancelar</Button>
              <Button type="submit" variant="contained" disabled={!dialogo.nome.trim()}>
                {dialogo.tipo === 'criar' ? 'Criar' : 'Salvar'}
              </Button>
            </DialogActions>
          </Box>
        ) : null}
      </Dialog>
    </Stack>
  )
}
